import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import axios from "axios";
import { Map, Upload, Trash2, CheckCircle, RefreshCw, ChevronDown } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const RouteManagementPage = ({ Layout }) => {
  const [routes, setRoutes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [inspectors, setInspectors] = useState([]);
  const [message, setMessage] = useState(null);

  // Form state
  const [selInspector, setSelInspector] = useState("");
  const [selDate, setSelDate] = useState(new Date().toISOString().slice(0, 10));
  const [selFile, setSelFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  // Filters
  const [filterInsp, setFilterInsp] = useState("");
  const [filterDate, setFilterDate] = useState("");

  // Inspector name lookup
  const inspLookup = useMemo(() => {
    const map = {};
    inspectors.forEach(i => { map[i.name] = i.fullName; });
    return map;
  }, [inspectors]);

  const fetchRoutes = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (filterInsp) params.append("inspector", filterInsp);
      if (filterDate) params.append("route_date", filterDate);
      const res = await axios.get(`${API}/routes?${params}`);
      setRoutes(res.data || []);
    } catch {} finally { setLoading(false); }
  }, [filterInsp, filterDate]);

  const fetchInspectors = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/team/inspectors`);
      const inspList = (res.data || []).filter(m => m.active === "yes").map(m => ({ name: m.name, fullName: m.full_name || m.name })).sort((a, b) => a.fullName.localeCompare(b.fullName));
      setInspectors(inspList);
    } catch {}
  }, []);

  useEffect(() => { fetchRoutes(); fetchInspectors(); }, [fetchRoutes, fetchInspectors]);

  const handleUpload = async () => {
    if (!selInspector || !selDate || !selFile) {
      setMessage({ type: "error", text: "Select inspector, date, and GPX file" });
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", selFile);
      fd.append("inspector", selInspector);
      fd.append("route_date", selDate);
      await axios.post(`${API}/routes`, fd);
      setMessage({ type: "success", text: `Route assigned to ${selInspector} for ${selDate}` });
      setSelFile(null);
      if (fileRef.current) fileRef.current.value = "";
      fetchRoutes();
    } catch (err) {
      setMessage({ type: "error", text: err.response?.data?.detail || "Upload failed" });
    } finally { setUploading(false); }
  };

  // 2026-05-20 — URGENT activation: parse GPX → route Active → daily_assignments
  // active → orders upsert with exact lat/lng in a single backend call.
  // Use when an inspector is in the field and no morning XLSX is available.
  const handleUploadAndActivate = async () => {
    if (!selInspector || !selDate || !selFile) {
      setMessage({ type: "error", text: "Select inspector, date, and GPX file" });
      return;
    }
    if (!window.confirm(
      `URGENT activation for ${selInspector} on ${selDate}?\n\n` +
      `This will:\n` +
      `  • Upload the GPX file\n` +
      `  • Mark the route status = Active\n` +
      `  • Create daily_assignments active = true for every waypoint\n` +
      `  • Upsert orders with exact lat/lng from the GPX\n\n` +
      `Use this only when the inspector is in the field and no XLSX import has been run.`
    )) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", selFile);
      fd.append("inspector", selInspector);
      fd.append("route_date", selDate);
      const { data } = await axios.post(`${API}/admin/upload-gpx-activate`, fd);
      setMessage({
        type: "success",
        text:
          `Activated ${selInspector} ${selDate} — ` +
          `${data.daily_assignments_inserted + data.daily_assignments_updated} stops, ` +
          `${data.orders_inserted} new orders, ${data.orders_updated} updated.`,
      });
      setSelFile(null);
      if (fileRef.current) fileRef.current.value = "";
      fetchRoutes();
    } catch (err) {
      setMessage({ type: "error", text: err.response?.data?.detail || "Activation failed" });
    } finally { setUploading(false); }
  };

  const toggleStatus = async (id, currentStatus) => {
    const newStatus = currentStatus === "Active" ? "Completed" : "Active";
    try {
      await axios.patch(`${API}/routes/${id}/status`, { status: newStatus });
      fetchRoutes();
    } catch {}
  };

  const deleteRoute = async (id) => {
    if (!window.confirm("Delete this route?")) return;
    try { await axios.delete(`${API}/routes/${id}`); fetchRoutes(); } catch {}
  };

  return (
    <Layout>
      <div className="space-y-5" data-testid="route-management-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-800">Route Management</h1>
            <p className="text-gray-500 text-sm mt-0.5">Upload and assign GPX routes to inspectors</p>
          </div>
          <button onClick={() => { setLoading(true); fetchRoutes(); }} className="px-4 py-2 bg-[#D1A054] text-white text-sm rounded-lg hover:bg-[#B8893E] flex items-center gap-1.5" data-testid="routes-refresh">
            <RefreshCw className="h-3.5 w-3.5" /> Refresh
          </button>
        </div>

        {message && (
          <div className={`px-4 py-3 rounded-lg text-sm font-medium flex items-center justify-between ${message.type === "success" ? "bg-green-50 text-green-800 border border-green-200" : "bg-red-50 text-red-800 border border-red-200"}`}>
            {message.text}
            <button onClick={() => setMessage(null)} className="text-xs opacity-50 hover:opacity-100">x</button>
          </div>
        )}

        {/* Upload Form */}
        <div className="bg-white rounded-xl border border-gray-200 p-5" data-testid="route-upload-form">
          <h2 className="text-sm font-bold text-gray-800 mb-4 flex items-center gap-2"><Map className="h-4 w-4 text-[#2F5D8A]" /> Assign New Route</h2>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
            <div>
              <label className="text-[10px] uppercase text-gray-400 font-semibold block mb-1">Inspector</label>
              <select value={selInspector} onChange={e => setSelInspector(e.target.value)} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm" data-testid="route-inspector-select">
                <option value="">Select Inspector</option>
                {inspectors.map(i => <option key={i.name} value={i.name}>{i.fullName} ({i.name})</option>)}
              </select>
            </div>
            <div>
              <label className="text-[10px] uppercase text-gray-400 font-semibold block mb-1">Route Date</label>
              <input type="date" value={selDate} onChange={e => setSelDate(e.target.value)} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm" data-testid="route-date-input" />
            </div>
            <div>
              <label className="text-[10px] uppercase text-gray-400 font-semibold block mb-1">GPX File</label>
              <input ref={fileRef} type="file" accept=".gpx,.xml" onChange={e => setSelFile(e.target.files[0] || null)} className="w-full px-3 py-1.5 border border-gray-200 rounded-lg text-sm file:mr-2 file:py-1 file:px-3 file:rounded file:border-0 file:bg-[#2F5D8A]/10 file:text-[#2F5D8A] file:text-xs file:font-semibold" data-testid="route-file-input" />
            </div>
            <button onClick={handleUpload} disabled={uploading || !selInspector || !selDate || !selFile} className="px-4 py-2 bg-[#2F5D8A] hover:bg-[#3B6D9C] text-white text-sm font-semibold rounded-lg disabled:opacity-40 flex items-center justify-center gap-1.5" data-testid="route-upload-btn">
              <Upload className="h-3.5 w-3.5" /> {uploading ? "Uploading..." : "Assign Route"}
            </button>
          </div>
          <div className="mt-3 flex items-center justify-end">
            <button
              onClick={handleUploadAndActivate}
              disabled={uploading || !selInspector || !selDate || !selFile}
              className="px-3 py-1.5 bg-red-600 hover:bg-red-700 text-white text-xs font-semibold rounded-lg disabled:opacity-40 flex items-center justify-center gap-1.5"
              title="URGENT: parse GPX, mark route Active, create daily_assignments active=true, and upsert orders with exact lat/lng — all in one call. Use when inspector is in the field."
              data-testid="route-upload-activate-btn"
            >
              <Upload className="h-3.5 w-3.5" /> {uploading ? "Working..." : "URGENT: Upload + Activate"}
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="flex items-center gap-3 flex-wrap">
          <select value={filterInsp} onChange={e => setFilterInsp(e.target.value)} className="px-3 py-1.5 border border-gray-200 rounded-lg text-xs" data-testid="routes-filter-inspector">
            <option value="">All Inspectors</option>
            {inspectors.map(i => <option key={i.name} value={i.name}>{i.fullName} ({i.name})</option>)}
          </select>
          <input type="date" value={filterDate} onChange={e => setFilterDate(e.target.value)} className="px-3 py-1.5 border border-gray-200 rounded-lg text-xs" data-testid="routes-filter-date" />
          {(filterInsp || filterDate) && (
            <button onClick={() => { setFilterInsp(""); setFilterDate(""); }} className="text-xs text-red-500 hover:underline">Clear</button>
          )}
        </div>

        {/* Routes Table */}
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center h-24"><div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#D1A054]" /></div>
          ) : routes.length === 0 ? (
            <div className="text-center py-8 text-gray-400 text-sm">No routes found.</div>
          ) : (
            <table className="w-full text-sm" data-testid="routes-table">
              <thead>
                <tr className="border-b border-gray-200 bg-gray-50/80">
                  <th className="px-4 py-2.5 text-left text-[10px] uppercase font-semibold text-gray-400">Inspector</th>
                  <th className="px-4 py-2.5 text-left text-[10px] uppercase font-semibold text-gray-400">Date</th>
                  <th className="px-4 py-2.5 text-left text-[10px] uppercase font-semibold text-gray-400">File</th>
                  <th className="px-4 py-2.5 text-center text-[10px] uppercase font-semibold text-gray-400">Status</th>
                  <th className="px-4 py-2.5 text-left text-[10px] uppercase font-semibold text-gray-400">Assigned By</th>
                  <th className="px-4 py-2.5 text-right text-[10px] uppercase font-semibold text-gray-400">Actions</th>
                </tr>
              </thead>
              <tbody>
                {routes.map(r => (
                  <tr key={r.id} className="border-b border-gray-100 hover:bg-gray-50" data-testid={`route-row-${r.id}`}>
                    <td className="px-4 py-2.5 font-medium text-gray-800">{inspLookup[r.inspector] || r.inspector} <span className="text-[10px] text-gray-400 font-normal">({r.inspector})</span></td>
                    <td className="px-4 py-2.5 text-gray-600">{r.route_date}</td>
                    <td className="px-4 py-2.5">
                      <a href={`${process.env.REACT_APP_BACKEND_URL}${r.gpx_url}`} download className="text-[#2F5D8A] hover:underline text-xs font-medium">{r.filename}</a>
                    </td>
                    <td className="px-4 py-2.5 text-center">
                      <button onClick={() => toggleStatus(r.id, r.status)} className={`px-2.5 py-0.5 rounded text-[10px] font-bold cursor-pointer ${r.status === "Active" ? "bg-green-50 text-green-700 hover:bg-green-100" : "bg-gray-100 text-gray-500 hover:bg-gray-200"}`} data-testid={`route-status-${r.id}`}>
                        {r.status}
                      </button>
                    </td>
                    <td className="px-4 py-2.5 text-gray-400 text-xs">{r.created_by}</td>
                    <td className="px-4 py-2.5 text-right">
                      <button onClick={() => deleteRoute(r.id)} className="p-1.5 rounded hover:bg-red-50 text-gray-400 hover:text-red-600" data-testid={`route-delete-${r.id}`}>
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default RouteManagementPage;
